import { Component, effect, inject, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, NavigationStart, Params, Router } from "@angular/router";
import { BreakpointObserver, BreakpointState, Breakpoints } from "@angular/cdk/layout";
import { Observable, Subscription, distinctUntilChanged } from "rxjs";
import { environment } from "../environments/environment";
import { UserStore, ScreenState, ScreenStore, initializeSentry } from "@shared/src/public-api";
import { GeneralStore } from "./stores/general.store";
import { GeneralRepository } from "./repositories/general/general-repository";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { ToastService } from "@shared/src/lib/services/toast.service";

@Component({
	selector: "jhmc-app-root",
	templateUrl: "./app.component.html",
	styleUrls: ["./app.component.scss"],
	standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
	public title: string = "Jail House Mail Civilian";
	public stripeTransactionId: string;
	public stripeStatus: string;
	public shouldShowNavBar: boolean = false;
	private route: ActivatedRoute;
	private router: Router;
	private Breakpoints = Breakpoints;
	private changesSubscription: Observable<BreakpointState>;
	readonly userStore = inject(UserStore);
	readonly screenStore = inject(ScreenStore);
	readonly generalStore = inject(GeneralStore);
	readonly generalRepository = inject(GeneralRepository);
	readonly toastService = inject(ToastService);
	private toastSubscription: Subscription;
	protected toastMessage: {
        message: string;
        type: "success" | "error" | "info";
    } | null = null;

	public constructor(router: Router, route: ActivatedRoute,
		private breakpointObserver: BreakpointObserver) {
		this.router = router;
		ModuleRegistry.registerModules([AllCommunityModule]);

		this.toastSubscription = this.toastService.toastState.subscribe((toast) => {
			this.toastMessage = toast;
		});

		effect(() => {
			const isLoggedIn: boolean = this.userStore.isLoggedIn();
			if (isLoggedIn) {
				this.generalRepository.getStartupInfo().subscribe((startupInfo) => {
					this.generalStore.setGeneralState({
						stripePublicKey: startupInfo.stripePublicKey,
						radarPublicKey: startupInfo.radarPublicKey
					});
				});

				try {
					if (Intercom) {
						this.initializeIntercom();
					}
				} catch (e) {
					return; // Let it go...
				}
			} else {
				if (Intercom) {
					Intercom("shutdown");
				}
			}
		})
		this.userStore.fetchUser();

		this.route = route;
		this.route.queryParams.subscribe((params: Params) => {
			if (params.stripe) {
				this.stripeStatus = params.stripe;
				if (params.transactionId) {
					this.stripeTransactionId = params.transactionId;
				}
			}
		});

		router.events.subscribe((event: any) => {
			if (event instanceof NavigationStart) {
				if (event.url.includes("login") || event.url.includes("sign-up")
					|| event.url.includes("privacy") || event.url.includes("terms") || event.url.includes("support")
					|| event.url.includes("landing") || event.url.includes("subscription")) {
					this.shouldShowNavBar = false;
				}
				else {
					this.shouldShowNavBar = true;
				}
			}
		});

		this.changesSubscription = this.breakpointObserver.observe([
			this.Breakpoints.Handset,
			this.Breakpoints.Tablet,
			this.Breakpoints.Web
		]).pipe(
			distinctUntilChanged()
		);

		this.changesSubscription.subscribe((_result: BreakpointState) => {
			this.breakpointChanged();
		});
	}

	ngOnInit(): void {
		if (environment.sentryEnabled) {
			initializeSentry(environment.env);
		}
	}

	ngOnDestroy(): void {
		if (this.toastSubscription) {
			this.toastSubscription.unsubscribe();
		}
	}

	clearToast(): void {
		this.toastMessage = null;
	}

	private breakpointChanged() {
		let size: ScreenState = null;
		size = {
			screenHeight: window.innerHeight,
			screenWidth: window.innerWidth,
			screenSize: null
		};
		if (this.breakpointObserver.isMatched(this.Breakpoints.Handset)) {
			size.screenSize = "MOBILE";
		}
		else if (this.breakpointObserver.isMatched(this.Breakpoints.Tablet)) {
			size.screenSize = "TABLET";
		}
		else if (this.breakpointObserver.isMatched(this.Breakpoints.Web)) {
			size.screenSize = "WEB";
		}
		else {
			size.screenSize = "WEB";
		}
		this.screenStore.setScreen(size);
	}

	public closeStripeModal(): void {
		this.stripeTransactionId = null;
		this.stripeStatus = null;
		// this clears out the query params
		this.router.navigate(
			[],
			{ relativeTo: this.route, queryParams: { stripe: null, transactionId: null } }
		);
	}

	private initializeIntercom(attempts = 0): any {
		if (attempts >= 3) {
			throw new Error();
		}
		try {
			Intercom("boot", {
				user_id: this.userStore.userId(),
				user_hash: this.userStore.intercomHash(),
				name: this.userStore.name(),
				email: this.userStore.username(),
				user_type: this.userStore.type()
			});
		} catch (e) {
			if (attempts < 3) {
				setTimeout(() => this.initializeIntercom(attempts + 1), 2000);
			}
		}
	}
}
